<template>
  <div class="space-y-6">
    <div class="bg-white rounded-lg border border-gray-200">
      <div class="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-200">
        <SearchableSelect v-model="filters.template" :options="templateChoices" placeholder="All templates" search-placeholder="Search templates" aria-label="Template" clearable class="w-56" />
        <select v-model="filters.executor" :class="[SIZED_INPUT, 'w-40']" aria-label="Executor">
          <option value="">All executors</option>
          <option v-for="e in executors" :key="e.id" :value="String(e.id)">{{ e.name }}</option>
        </select>
        <select v-model="filters.status" :class="[SIZED_INPUT, 'w-36']" aria-label="Status">
          <option value="">Any status</option>
          <option v-for="s in statuses" :key="s" :value="s">{{ words(s) }}</option>
        </select>
        <select v-model="filters.trigger" :class="[SIZED_INPUT, 'w-36']" aria-label="Trigger">
          <option value="">Any trigger</option>
          <option v-for="t in triggers" :key="t" :value="t">{{ words(t) }}</option>
        </select>
        <label class="text-gray-500">From <input v-model="filters.since" type="date" :class="[INPUT, 'inline-block w-40 ml-2']"></label>
        <label class="text-gray-500">To <input v-model="filters.until" type="date" :class="[INPUT, 'inline-block w-40 ml-2']"></label>
        <button v-if="anyFilter" type="button" :class="TEXT_BUTTON" @click="reset">Reset filters</button>
        <button type="button" :class="[PRIMARY_BUTTON, 'ml-auto']" @click="openStart()">Start run</button>
      </div>

      <div v-if="error" class="m-4 rounded-md bg-red-tint px-4 py-3 text-gray-900">
        <strong>Runs could not be loaded.</strong> {{ error }} <button type="button" :class="[TEXT_BUTTON, 'ml-3']" @click="load">Retry</button>
      </div>
      <table v-else class="w-full table-fixed divide-y divide-gray-200">
        <colgroup>
          <col style="width:8%"><col style="width:14%"><col style="width:14%"><col style="width:14%"><col style="width:14%">
          <col style="width:6%"><col style="width:7%"><col style="width:8%"><col style="width:7%"><col style="width:8%">
        </colgroup>
        <thead class="bg-gray-50"><tr>
          <th :class="TH">Run</th><th :class="TH">Template</th><th :class="TH">Executor</th><th :class="TH">Trigger</th><th :class="TH">Status</th>
          <th :class="[TH, 'text-right']">Results</th><th :class="[TH, 'text-right']">Cost</th><th :class="TH">Duration</th><th :class="TH">Started</th>
          <th :class="[TH, 'text-right']">Actions</th>
        </tr></thead>
        <tbody class="divide-y divide-gray-200">
          <template v-if="loading && !runs.length">
            <tr v-for="n in 4" :key="n"><td v-for="c in 10" :key="c" :class="TD"><div class="h-4 rounded bg-gray-100" /></td></tr>
          </template>
          <tr v-else-if="!runs.length"><td colspan="10" class="px-3 py-8 text-center text-gray-500">No runs match these filters.</td></tr>
          <tr v-for="r in runs" v-else :key="r.id" class="cursor-pointer hover:bg-gray-50" @click="open(r)">
            <td :class="[TD, r.status === 'running' ? 'border-l-2 border-neutral-700' : '']"><span class="truncate block" :title="`Run ${r.id}`">#{{ r.id }}</span></td>
            <td :class="TD">{{ r.template_name || 'No template' }}<div class="text-gray-500">{{ r.is_draft ? `draft “${r.draft_name}” · ` : "" }}{{ r.version ? `v${r.version}` : "unnumbered" }}</div></td>
            <td :class="TD">{{ r.executor_key }}<div class="text-gray-500 truncate" :title="r.executor_model">{{ r.executor_model }}</div></td>
            <td :class="TD">{{ words(r.trigger) }}<div v-if="r.requested_by_name" class="text-gray-500 sensitive">{{ r.requested_by_name }}</div></td>
            <td :class="TD">
              <HermesBadge :value="r.status" />
              <div class="text-gray-600 tabular-nums mt-1">{{ progressLine(r) }}</div>
              <div v-if="r.pending_effects" class="text-gray-600 mt-1">{{ r.pending_effects }} waiting for approval</div>
            </td>
            <td :class="[TD, 'text-right tabular-nums']">{{ r.results }}</td>
            <td :class="[TD, 'text-right tabular-nums']">{{ money((r.usage || {}).cost_usd) }}<div v-if="r.status === 'running'" class="text-gray-500">so far</div></td>
            <td :class="[TD, 'tabular-nums']">{{ duration(r.started_at, r.finished_at) }}</td>
            <td :class="TD" :title="absolute(r.created_at)">{{ relative(r.created_at) }}</td>
            <td :class="[TD, 'text-right whitespace-nowrap']" @click.stop>
              <router-link :to="{ name: 'AdminHermesRun', params: { runId: r.id } }" :class="TEXT_BUTTON">Open</router-link>
              <button v-if="['pending', 'running'].includes(r.status)" type="button" :class="[TEXT_BUTTON, 'ml-3']" @click="cancel(r)">Cancel</button>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="count > limit" class="flex items-center gap-3 px-4 py-3 border-t border-gray-200 text-gray-600">
        {{ offset + 1 }}–{{ Math.min(offset + limit, count) }} of {{ count }}
        <button type="button" :class="[TEXT_BUTTON, 'ml-auto']" :disabled="!offset" @click="page(-1)">Previous</button>
        <button type="button" :class="TEXT_BUTTON" :disabled="offset + limit >= count" @click="page(1)">Next</button>
      </div>
    </div>

    <HermesStartPanel v-if="starting" :executors="executors" :templates="templates" :template-id="startTemplate" @close="starting = false" />
  </div>
</template>

<script>
import queryState from '@/mixins/queryState';
import HermesBadge from './HermesBadge.vue';
import HermesStartPanel from './HermesStartPanel.vue';
import SearchableSelect from '@/components/widgetcomponents/library/SearchableSelect.vue';
import { API, INPUT, SIZED_INPUT, PRIMARY_BUTTON, TD, TEXT_BUTTON, TH, absolute, duration, errorOf, money, progressLine, relative, templateOptions, words } from './hermes';

const EMPTY = { template: '', executor: '', status: '', trigger: '', since: '', until: '' };

export default {
  name: 'HermesRuns',
  components: { HermesBadge, HermesStartPanel, SearchableSelect },
  mixins: [queryState('filters', EMPTY)],
  data() {
    return {
      runs: [], count: 0, offset: 0, limit: 50, loading: true, error: '', timer: null,
      executors: [], templates: [], starting: false, startTemplate: null,
      statuses: ['pending', 'running', 'completed', 'failed', 'cancelled'], triggers: ['person', 'schedule', 'event', 'executor'],
      INPUT, SIZED_INPUT, PRIMARY_BUTTON, TD, TEXT_BUTTON, TH,
    };
  },
  computed: {
    templateChoices() {
      return templateOptions(this.templates);
    },
    anyFilter() {
      return Object.values(this.filters).some(Boolean);
    },
  },
  watch: {
    '$route.query.start'(id) {
      if (id && this.templates.length) this.consumeStart();
    },
    filters: {
      deep: true,
      handler() {
        this.offset = 0;
        this.load();
      },
    },
  },
  async mounted() {
    const [e, t] = await Promise.all([this.$axios.get(`${API}/executors/`), this.$axios.get(`${API}/templates/`)]);
    this.executors = e.data;
    this.templates = t.data;
    this.consumeStart();
    this.load();
  },
  beforeUnmount() {
    clearTimeout(this.timer);
  },
  methods: {
    absolute, duration, money, progressLine, relative, words,
    async load() {
      clearTimeout(this.timer);
      this.loading = true;
      this.error = '';
      const params = { limit: this.limit, offset: this.offset };
      Object.entries(this.filters).forEach(([k, v]) => {
        if (!v) return;
        if (k === 'since') params.since = `${v}T00:00:00Z`;
        else if (k === 'until') params.until = `${v}T23:59:59Z`;
        else params[k] = v;
      });
      try {
        const { data } = await this.$axios.get(`${API}/runs/`, { params });
        this.runs = data.results;
        this.count = data.count;
        if (this.runs.some((r) => ['pending', 'running'].includes(r.status))) this.timer = setTimeout(this.load, 5000);
      } catch (e) {
        this.error = errorOf(e, 'Try again in a moment.');
      } finally {
        this.loading = false;
      }
    },
    reset() {
      this.filters = { ...EMPTY };
    },
    page(dir) {
      this.offset = Math.max(0, this.offset + dir * this.limit);
      this.load();
    },
    open(r) {
      this.$router.push({ name: 'AdminHermesRun', params: { runId: r.id } });
    },
    consumeStart() {
      const id = this.$route.query.start;
      if (!id) return;
      this.openStart(Number(id));
      const query = { ...this.$route.query };
      delete query.start;
      this.$router.replace({ query });
    },
    openStart(templateId = null) {
      this.startTemplate = templateId;
      this.starting = true;
    },
    async cancel(r) {
      if (!window.confirm(`Cancel run #${r.id}? Units already sent to the model finish; nothing new starts.`)) return;
      try {
        await this.$axios.post(`${API}/runs/${r.id}/cancel/`);
      } catch (e) {
        window.alert(errorOf(e, 'The run could not be cancelled.'));
      }
      this.load();
    },
  },
};
</script>
